import React, { FC, useEffect, useState } from "react";
import { observer } from "mobx-react";
import DeleteIcon from "../../../../../../images/products/trash-blue.svg";
import EditIcon from "../../../../../../images/products/edit-blue.svg";
import { NavigateFunction, Params } from "react-router-dom";
import { firstValueFrom } from "rxjs";
import DeleteCheckoutPopup from "./DeleteCheckoutPopup";
import {
  CheckoutIframeDetailPage,
  DashboardTopBar,
  ItemContentArea,
  NavPills,
  RelatedAddOnsGrid,
  RelatedItemsGrid,
  RelatedPromoCodesGrid,
  SharingSection,
  SplitDropDown,
} from "../../../shared/components";
import {
  AuthService,
  CustomLabel,
  FlexContainer,
  NavigationButtons,
  ToastService,
  ToggleSwitch,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  AccountSettingsStore,
  ItemsStore,
  PurchaseStore,
  RolesAndPermissionStore,
} from "../../../shared/store";
import { CheckOutService } from "../../../shared/services";
import {
  CheckoutMainDetailPageModel,
  CustomerFacingCheckOutItemModel,
  CustomerFacingCheckOutModel,
  displayCardData,
  getPriceByBillingType,
  RelatedAddOnsGridModel,
  RelatedItemsCategoriesModel,
  RelatedItemsGridModel,
  SelectedItemModel,
} from "../../../shared/models";
import {
  CheckOutDisplayTypes,
  ItemTypes,
  SystemItemTypes,
} from "../../../shared/enums";
import {
  CheckoutDetailPreviewSection,
  DescriptionCell,
  ItemDetailLeftPanel,
  ItemDetailMainWrapper,
  ItemDetailRightPanel,
  LabelCell,
  MainOuterCard,
  SubscriptionStylesWrapper,
} from "../../../shared/styles";
import { MerchantAccountStore } from "../../../modules/merchantAccounts";

type IProps = {
  id: string;
  params: Readonly<Params<string>>;
  purchaseStore: PurchaseStore;
  itemStore: ItemsStore;
  navigate: NavigateFunction;
  rolesAndPermissionStore: RolesAndPermissionStore;
  merchantAccountStore: MerchantAccountStore;
};

const CheckoutcheckoutDetailPageMain: FC<IProps> = ({
  purchaseStore,
  itemStore,
  navigate,
  params,
  rolesAndPermissionStore,
  merchantAccountStore,
}) => {
  const [activeNavPill, setActiveNavPill] = useState("details");
  const [showLoader, setShowLoader] = useState(true);
  const [showDeletePopup, setShowDeletePopup] = React.useState(false);

  const getCheckoutId = () => {
    return params.id || "";
  };

  useEffect(() => {
    onRefresh();
    return () => {
      itemStore?.setBreadCrumbData();
    };
  }, [getCheckoutId()]);

  const onRefresh = async () => {
    setShowLoader(true);
    const response = await firstValueFrom(
      CheckOutService.getCustomerFacingCheckOutDetails(
        getCheckoutId(),
        itemStore.checkoutSearchValue,
      ),
    );
    if (response.result) {
      setShowLoader(false);
      purchaseStore.deserializeCustomerFacingCheckOutModel(response.result);
      // Load account settings with the correct account ID from the response
      AccountSettingsStore.loadGeneralSettings(response.result.accountId);
      itemStore?.setBreadCrumbData(
        new SelectedItemModel({
          route: "/app/checkouts",
          title: purchaseStore.customerFacingCheckOutDetails.name,
          icon: "",
        }),
      );
    }
  };

  const getPriceOrVariant = (item: CustomerFacingCheckOutItemModel) => {
    if (item.hasVariants) {
      return `${item.variants.options.length} variants`;
    } else {
      return `${item.price.name}`;
    }
  };

  const getPriceDescription = (item: CustomerFacingCheckOutItemModel) => {
    return getPriceByBillingType(
      item.itemType,
      item.hasVariants,
      item.variants,
      item.price.id,
      item.price.billingType,
      item.price.terms,
      item.price.recurrence,
      item.quantity,
      0,
    );
  };

  const getPriceDescriptionForAddons = (item: RelatedItemsCategoriesModel) => {
    return getPriceByBillingType(
      item.type,
      item.hasVariants,
      item.variants,
      item.prices[0].id,
      item.prices[0].billingType,
      item.prices[0].terms,
      item.prices[0].recurrence,
      parseFloat(item.quantity),
    );
  };

  const getOneTimePriceDetails = (item: CustomerFacingCheckOutItemModel) => {
    const priceDescription = getPriceDescription(item);
    if (!priceDescription) return "-";

    switch (item.price.billingType) {
      case "Term":
        return `${priceDescription.priceWithDiscount || priceDescription.price} `;
      default:
        return "-";
    }
  };

  const getOneTimePriceDetailsForAddons = (
    item: RelatedItemsCategoriesModel,
  ) => {
    const priceDescription = getPriceDescriptionForAddons(item);
    if (!priceDescription) return "-";

    switch (item.prices[0].billingType) {
      case "Term":
        return `${priceDescription.priceWithDiscount || priceDescription.price} `;
      default:
        return "-";
    }
  };

  const getRecurringPriceDetails = (item: CustomerFacingCheckOutItemModel) => {
    const priceDescription = getPriceDescription(item);
    if (!priceDescription) return "-";
    switch (item.price.billingType) {
      case "Recurring":
        let recurringPrice = `${priceDescription.priceWithDiscount || priceDescription.price}`;
        if (item.price.recurrence?.standardPricing) {
          const price = AccountSettingsStore.formatAmount(
            parseFloat(
              item.price.recurrence?.standardPricing?.price?.toString() || "0",
            ),
            true,
          );
          recurringPrice += ` + $${price} every ${item.price.recurrence.billingIntervalCount} ${item.price.recurrence.billingInterval?.toLocaleLowerCase()}(s)`;
        }
        return recurringPrice;
      case "TermPlusRecurring":
        let termPlusRecurringPrice = `${priceDescription.priceWithDiscount || priceDescription.price} + `;
        termPlusRecurringPrice += `${priceDescription.priceWithDiscount || priceDescription.price} every `;
        termPlusRecurringPrice += `${item.price.recurrence.billingIntervalCount} ${item.price.recurrence.billingInterval?.toLocaleLowerCase()}(s)`;
        return termPlusRecurringPrice;
      default:
        return "-";
    }
  };

  const getRecurringPriceDetailsForAddons = (
    item: RelatedItemsCategoriesModel,
  ) => {
    const priceDescription = getPriceDescriptionForAddons(item);
    if (!priceDescription) return "-";
    switch (item.prices[0].billingType) {
      case "Recurring":
        let recurringPrice = `${priceDescription.priceWithDiscount || priceDescription.price}`;
        if (item.prices[0].recurrence?.standardPricing) {
          const price = AccountSettingsStore.formatAmount(
            parseFloat(
              item.prices[0].recurrence?.standardPricing?.price?.toString() ||
                "0",
            ),
            true,
          );
          recurringPrice += ` + $${price} every ${item.prices[0].recurrence.billingIntervalCount} ${item.prices[0].recurrence.billingInterval?.toLocaleLowerCase()}(s)`;
        }
        return recurringPrice;
      case "TermPlusRecurring":
        let termPlusRecurringPrice = `${priceDescription.priceWithDiscount || priceDescription.price} + `;
        termPlusRecurringPrice += `${priceDescription.priceWithDiscount || priceDescription.price} every `;
        termPlusRecurringPrice += `${item.prices[0].recurrence.billingIntervalCount} ${item.prices[0].recurrence.billingInterval?.toLocaleLowerCase()}(s)`;
        return termPlusRecurringPrice;
      default:
        return "-";
    }
  };

  const relatedItems = (): RelatedItemsGridModel[] => {
    return purchaseStore.customerFacingCheckOutDetails.items.map(
      (item) =>
        new RelatedItemsGridModel({
          id: item.id,
          name: item.name,
          type:
            (item.itemType as keyof typeof SystemItemTypes) ||
            SystemItemTypes.Product,
          oneTimePrice: getOneTimePriceDetails(item),
          recurringPrice: getRecurringPriceDetails(item),
          priceOrvariant: getPriceOrVariant(item),
        }),
    );
  };

  const mapToRelatedAddOnsGridModel = (
    item: RelatedItemsCategoriesModel,
    type: string,
    count: number,
    itemName: string,
    amount: string,
    recurringPrice: string,
    itemType: string,
  ): RelatedAddOnsGridModel => {
    return new RelatedAddOnsGridModel({
      id: item.id,
      title: type,
      name: itemName,
      itemCount: count,
      amount: amount,
      recurringPrice: recurringPrice,
      type: itemType as keyof typeof ItemTypes,
    });
  };

  const getData = (
    items: CustomerFacingCheckOutItemModel[],
  ): RelatedAddOnsGridModel[] => {
    const list: RelatedAddOnsGridModel[] = [];

    const addRelatedItems = (
      item: CustomerFacingCheckOutItemModel,
      relatedItems: RelatedItemsCategoriesModel[],
      type: string,
    ) => {
      if (relatedItems && relatedItems.length > 0) {
        relatedItems.forEach((relatedItem) => {
          list.push(
            mapToRelatedAddOnsGridModel(
              relatedItem,
              type,
              relatedItems.length,
              relatedItem.name,
              getOneTimePriceDetailsForAddons(relatedItem),
              getRecurringPriceDetailsForAddons(relatedItem),
              relatedItem.type,
            ),
          );
        });
      }
    };

    items.forEach((item) => {
      const relatedItems = item.relatedItems;

      if (relatedItems) {
        if (item.includedItems && item.includedItems.length > 0) {
          addRelatedItems(item, item.includedItems, "Included");
        }
        if (item.requiredItems && item.requiredItems.length > 0) {
          addRelatedItems(item, item.requiredItems, "Required");
        }
        if (item.recommendedItems && item.recommendedItems.length > 0) {
          addRelatedItems(item, item.recommendedItems, "Recommended");
        }
      }
    });

    const uniqueList = Array.from(
      new Map(list.map((item) => [JSON.stringify(item), item])).values(),
    );
    return uniqueList;
  };

  const row = (
    label: string,
    rightColumn: string | React.ReactNode,
    className?: string,
  ): React.ReactNode => {
    return (
      <FlexContainer className={`label-description-row ${className}`}>
        <LabelCell>
          <CustomLabel>{label}</CustomLabel>
        </LabelCell>
        <DescriptionCell>{rightColumn}</DescriptionCell>
      </FlexContainer>
    );
  };

  const navPills = () => {
    return (
      <FlexContainer alignItems="center" padding="0px 0px 50px">
        <CustomLabel color="#2C3E50" fontSize="30px" fontWeight="600">
          Checkout
        </CustomLabel>
        <div className="flex-auto-grow">
          <NavPills
            defaultActiveKey="details"
            pills={[
              {
                key: "details",
                title: "DETAILS",
              },
              {
                key: "orders",
                title: "ORDERS",
              },
            ]}
            onSelect={(key) => setActiveNavPill(key)}
          />
        </div>
      </FlexContainer>
    );
  };

  const actionDropdown = () => {
    return (
      <SplitDropDown
        label="Edit"
        onClick={() => {
          if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
          navigate(`/app/checkout/detail/${getCheckoutId()}`);
        }}
        className="edit-dropdown-item"
        options={[
          {
            icon: EditIcon,
            label: "Edit",
            value: "edit",
            onClick: () => {
              if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
              navigate(`/app/checkout/detail/${getCheckoutId()}`);
            },
          },
          {
            icon: DeleteIcon,
            label: "Delete",
            value: "Delete",
            onClick: () => {
              if (!rolesAndPermissionStore.hasManagePermission("Delete"))
                return;
              setShowDeletePopup(true);
            },
          },
        ]}
      />
    );
  };

  const getImage = (type: keyof typeof CheckOutDisplayTypes) => {
    return displayCardData.single
      .concat(displayCardData.multiple)
      .find((d) => d.id === type)?.image;
  };

  function updateStatus() {
    if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
    CheckOutService.updateStatus(
      AuthService.authData.accountId,
      getCheckoutId(),
      !purchaseStore.customerFacingCheckOutDetails.isActive,
    ).subscribe({
      next: (result) => {
        if (!result.result) {
          ToastService.showError("Failed to update status");
        } else {
          purchaseStore.updateCustomerFacingCheckOutModel(
            new CustomerFacingCheckOutModel({
              ...purchaseStore.customerFacingCheckOutDetails,
              isActive: !purchaseStore.customerFacingCheckOutDetails.isActive,
            }),
          );
        }
      },
    });
  }

  const handleInputChange = (
    field: keyof CheckoutMainDetailPageModel,
    value: string | boolean,
  ) => {
    purchaseStore.updateDetailPageMain(
      new CheckoutMainDetailPageModel({
        ...purchaseStore.checkoutDetailPageMain,
        [field]: value,
      }),
    );
  };

  function getLayoutName() {
    if (purchaseStore.customerFacingCheckOutDetails.isDonationOnly)
      return "Donation";

    if (purchaseStore.customerFacingCheckOutDetails.showSingleItemPrice)
      return "Single Item & Price";

    switch (
      CheckOutDisplayTypes[
        purchaseStore.customerFacingCheckOutDetails.displayType
      ]
    ) {
      case "SingleItem":
        return "Single Item & Price";
      case "SingleItemWithMultiplePrices":
        return "Single Item / Multiple Prices";
      case "MultipleItems":
        return "Multiple Items";
      case "MultipleItemsInbundle":
        return "Multiple Items in a Bundle";
      default:
        return "";
    }
  }

  function toggleDeletePopup(show: boolean, isDeleted: boolean) {
    setShowDeletePopup(show);
    if (isDeleted) {
      navigate("/app/checkouts");
    }
  }

  return (
    <>
      {showDeletePopup && (
        <DeleteCheckoutPopup
          id={getCheckoutId()}
          onClose={(isDeleted) => toggleDeletePopup(false, isDeleted)}
        />
      )}
      <DashboardTopBar
        itemStore={itemStore}
        merchantAccountStore={merchantAccountStore}
        rolesAndPermissionStore={rolesAndPermissionStore}
        rightContent={
          <>
            <NavigationButtons
              currentIndex={purchaseStore.customerFacingCheckOutDetails.index}
              totalItems={
                purchaseStore.customerFacingCheckOutDetails.totalCheckouts
              }
              previousRecordId={
                purchaseStore.customerFacingCheckOutDetails.previousRecordId
              }
              nextRecordId={
                purchaseStore.customerFacingCheckOutDetails.nextRecordId
              }
              onNavigate={(id) => navigate(`/app/checkouts/detail/${id}`)}
            />
            {actionDropdown()}
          </>
        }
      />
      <ItemContentArea
        itemStore={itemStore}
        showLoader={showLoader}
        showMerchantAccountNotificationBar={merchantAccountStore.hasNotificationBar()}
        node={
          <ItemDetailMainWrapper>
            <SubscriptionStylesWrapper className="purchase-link-detail-page">
              {" "}
              <MainOuterCard>
                {false && navPills()}
                {activeNavPill === "details" && (
                  <FlexContainer>
                    <ItemDetailLeftPanel className="preview-left-panel">
                      <CustomLabel className="pl-sub-header">
                        Details
                      </CustomLabel>
                      {row(
                        "Status",
                        <ToggleSwitch
                          on={
                            purchaseStore.customerFacingCheckOutDetails
                              .isActive || false
                          }
                          onChange={() => updateStatus()}
                        />,
                        "pl-status-label",
                      )}
                      {row(
                        "Name",
                        <FlexContainer alignItems="center" spacing={13}>
                          <CustomLabel>
                            {purchaseStore.customerFacingCheckOutDetails.name}
                          </CustomLabel>
                          {/* <img src={EditIcon} className="clickable" /> */}
                        </FlexContainer>,
                      )}
                      {row(
                        "Layout",
                        <FlexContainer fxLayout="column" spacing={10}>
                          <CustomLabel>{getLayoutName()}</CustomLabel>
                          <img
                            src={getImage(
                              purchaseStore.customerFacingCheckOutDetails
                                .displayType,
                            )}
                            alt="icon"
                          />
                        </FlexContainer>,
                      )}
                      {row(
                        "Merchant",
                        <CustomLabel>
                          {" "}
                          {
                            purchaseStore.customerFacingCheckOutDetails
                              .merchantAccount.name
                          }
                        </CustomLabel>,
                      )}
                      {row(
                        "Seller",
                        purchaseStore.customerFacingCheckOutDetails.seller
                          ?.name || "",
                      )}
                      {row(
                        "Checkout ID",
                        <CustomLabel> {getCheckoutId()}</CustomLabel>,
                      )}
                      {row(
                        "Created Date",
                        <CustomLabel>
                          {UtilityMethods.formatDateInStringWithTime(
                            purchaseStore.customerFacingCheckOutDetails
                              .createdDate,
                          )}
                        </CustomLabel>,
                      )}
                      {row("Created By", "-")}
                      <SharingSection
                        // handleInputChange={handleInputChange}
                        // detailPage={purchaseStore.checkoutDetailPageMain}
                        name={purchaseStore.customerFacingCheckOutDetails.name}
                        id={getCheckoutId()}
                        isCheckout={true}
                        purchaseURL={
                          purchaseStore.customerFacingCheckOutDetails
                            .purchaseBaseURL +
                          "/app/purchase/checkout/" +
                          getCheckoutId()
                        }
                      />
                      {/* <FlexContainer>{renderSharingSection()}</FlexContainer> */}
                    </ItemDetailLeftPanel>
                    <ItemDetailRightPanel
                      borderLeft="none"
                      className="preview-detail-section"
                      padding="0px 0px 0px 25px"
                    >
                      <CustomLabel
                        fontWeight={"600"}
                        fontSize={"20px"}
                        margin="0px 0px 20px 0px"
                      >
                        Preview
                      </CustomLabel>
                      <CheckoutDetailPreviewSection>
                        <CheckoutIframeDetailPage
                          purchaseStore={purchaseStore}
                        />
                      </CheckoutDetailPreviewSection>
                    </ItemDetailRightPanel>
                  </FlexContainer>
                )}
              </MainOuterCard>
              <RelatedPromoCodesGrid
                promoCodes={purchaseStore.checkoutDetailPageMain.promoCodes}
                countriesWithState={itemStore.countryStore.countriesWithState}
              />
              <RelatedItemsGrid
                items={relatedItems()}
                countriesWithState={itemStore.countryStore.countriesWithState}
              />
              <RelatedAddOnsGrid
                items={getData(
                  purchaseStore.customerFacingCheckOutDetails.items,
                )}
                rolesAndPermissionStore={rolesAndPermissionStore}
                countriesWithState={itemStore.countryStore.countriesWithState}
              />
            </SubscriptionStylesWrapper>
          </ItemDetailMainWrapper>
        }
      ></ItemContentArea>
    </>
  );
};

export default withRouter(observer(CheckoutcheckoutDetailPageMain));
